import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import type { MafiaState } from "../gen/bazivan/v1/table_pb";
import { client } from "../net/client";
import { signal, useSignal, type Signal } from "../net/signal";
import { coolProps, useCool } from "../net/pace";
import { Overlay } from "../ui/Overlay";
import { ResultAd } from "../ui/Ads";
import { CupAfter } from "../ui/CupBits";
import { ChatFab, ChatLive } from "../ui/Chat";
import { avatarSrc } from "./avatar";
import { botBare, botText } from "./seats";
import { BotIcon } from "../ui/BotIcon";
import { fa } from "./cards";
import { K } from "./speed";
import { MafiaOnline, REACTIONS, reactKey, type Fx } from "../mafia/controller";
import { SCENARIOS } from "../mafia/data";
import {
  Ask, CARD_DESC, Card, NOON_TITLE, SAY_LABEL, Say, StepKind, TEAM_ACT_FA, Talk, Team, TeamAct, dayTitle, instruction, isMafiaRole,
  nightTitle, roleDesc, roleFa, roleKey, roleTeam, scenarioFa, teamFa, wireFa,
} from "../mafia/text";
import { CountryFlag } from "../ui/CountryFlag";

/**
 * میزِ مافیا روی وب — همتای MafiaView ِ اپ: صندلی‌ها روی بیضی (تو پایین)، گرداننده وسط، تابلوی پرسش
 * پایین. انتخاب روی خودِ چهره‌هاست، نه دکمه‌ی جدا — همان قراردادِ اپ. قاعده و نقش‌ها روی سرور است
 * (Backend/internal/table/mafia.go)؛ این‌جا فقط نمای همین بازیکن کشیده می‌شود.
 */
export function MafiaTable({ state }: { state: MafiaState }) {
  const sig = useRef<Signal<MafiaOnline | null> | null>(null);
  if (!sig.current) sig.current = signal<MafiaOnline | null>(null);
  useEffect(() => {
    const c = new MafiaOnline();
    sig.current!.value = c;
    c.start();
    return () => { c.dispose(); sig.current!.value = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="table mf" data-table={state.tableId}>
      <Live sig={sig.current} />
    </div>
  );
}

function Live({ sig }: { sig: Signal<MafiaOnline | null> }) {
  const c = useSignal(sig);
  if (!c) return null;
  return <LiveInner c={c} />;
}

// ─────────────────────────── پرسش ───────────────────────────

type Choice = { label: string; value: number };
type Spec = {
  mode: "pick" | "talk" | "hand" | "suggest";
  title: string;
  choices: Choice[];
  legal: number[];
  legal2: number[];
  min: number;
  max: number;
  optional: boolean;
  needsTarget: (ch: number) => boolean;
  needsRole: (ch: number) => boolean;
  needsSecond: (ch: number) => boolean;
  roles: number[];
};

const uniq = (l: number[]) => [...new Set(l)];

/** خواسته‌ی من ← آن‌چه تابلو می‌کشد (همان MafiaAskSpecs ِ اپ). */
export function specOf(m: MafiaState): Spec | null {
  const p = m.prompt;
  const base: Spec = {
    mode: "pick", title: "", choices: [], legal: [], legal2: [], min: 1, max: 1, optional: true,
    needsTarget: () => false, needsRole: () => false, needsSecond: () => false, roles: [],
  };
  if (!p || m.watching) {
    // مافیای غیرِتصمیم‌گیر: پیشنهاد به تیم
    if (!m.watching && m.night && m.teamLeader >= 0 && m.teamLeader !== m.me && m.mseats[m.me]?.alive && m.mates.length > 0) {
      const legal = m.mseats.map((s, i) => (s.alive && i !== m.me && !m.mates.includes(i) ? i : -1)).filter((i) => i >= 0);
      return { ...base, mode: "suggest", title: `${m.seats[m.teamLeader]?.name ?? ""} تصمیم می‌گیرد — پیشنهادت؟`, legal, needsTarget: () => true };
    }
    return null;
  }
  if (p.blocked) return null;
  const s: Spec = { ...base, title: instruction(p.ask, p.min, p.max), legal: [...p.legal], legal2: [...p.legal2], min: p.min, max: p.max, optional: p.optional };
  const roles = uniq([...m.lineup].filter((r) => r !== 80));
  switch (p.ask) {
    case Ask.Speak: return { ...s, mode: "talk" };
    case Ask.Vote: return { ...s, mode: "hand" };
    case Ask.Status:
      return { ...s, choices: [{ label: "بله، استعلام", value: 1 }, { label: "نه", value: 0 }] };
    case Ask.Team: {
      const choices: Choice[] = [];
      for (let a = 1; a <= 6; a++) if (p.teamActs & (1 << a)) choices.push({ label: TEAM_ACT_FA[a] ?? "", value: a });
      return { ...s, choices, needsTarget: () => true, needsRole: (c) => c === TeamAct.Sixth || c === TeamAct.Nato,
        needsSecond: (c) => c === TeamAct.Sodagari, roles };
    }
    case Ask.GiveGun: {
      // extra: بیت‌های ۰ تا ۷ جنگیِ مانده، بیتِ ۸ «مشقی تمام شد» (زودیاک)
      const choices: Choice[] = [];
      if ((p.extra & 0xff) > 0) choices.push({ label: "جنگی", value: 1 });
      if (!(p.extra & 256)) choices.push({ label: "مشقی", value: 2 });
      return { ...s, choices, needsTarget: () => true };
    }
    case Ask.Bomb:
      // رنگِ سیمِ خنثی‌کننده (رمز)، بعد صاحبِ بمب
      return { ...s, choices: [1, 2, 3, 4].map((k) => ({ label: `سیم ${wireFa(k)}`, value: k })), needsTarget: () => true };
    case Ask.Defuse:
    case Ask.Wire:
      return { ...s, choices: [1, 2, 3, 4].map((k) => ({ label: wireFa(k), value: k })) };
    case Ask.Mayor:
      return { ...s, choices: [{ label: "لغو رأی‌گیری", value: 1 }, { label: "بیرون بفرست…", value: 2 }, { label: "کاری نمی‌کنم", value: 0 }],
        needsTarget: (c) => c === 2 };
    case Ask.InterGo: return { ...s, choices: [{ label: "ادامه", value: 1 }, { label: "لغو", value: 0 }] };
    case Ask.DieHard: return { ...s, choices: [{ label: "استعلام بگیر", value: 1 }, { label: "این شب نه", value: 0 }] };
    case Ask.NostraSide: {
      const choices: Choice[] = [];
      if (p.min === 0) choices.push({ label: "با شهر", value: 0 });
      choices.push({ label: "با مافیا", value: 1 });
      return { ...s, choices };
    }
    case Ask.Card:
      return { ...s, title: CARD_DESC[m.card] ?? s.title, needsTarget: () => s.legal.length > 0,
        needsRole: () => m.card === Card.MindShab, roles };
  }
  return { ...s, needsTarget: () => s.legal.length > 0 };
}

// ─────────────────────────── میز ───────────────────────────

function LiveInner({ c }: { c: MafiaOnline }) {
  const m = useSignal(c.model);
  const ended = useSignal(c.ended);
  const reveal = useSignal(c.reveal);
  const deal = useSignal(c.deal);
  const sent = useSignal(c.sent);
  const phase = useSignal(c.phase);
  const [book, setBook] = useState(false);
  const [roleCard, setRoleCard] = useState(false);
  // انتخابِ همین پرسش — با گامِ تازه پاک می‌شود
  const [sel, setSel] = useState<number[]>([]);
  const [choice, setChoice] = useState(-1);
  const [role, setRole] = useState(0);
  const [second, setSecond] = useState(-1);
  const [pop, setPop] = useState(-1);
  const [picker, setPicker] = useState(false);
  const [toast, setToast] = useState<{ text: string; key: number } | null>(null);
  // واکنش: صندلیِ هدف (خودم = ایموجی بالای صندلیِ خودم)، −۱ = بسته
  const [rpop, setRpop] = useState(-1);
  const askKey = m ? `${m.stepNo}|${m.prompt?.ask ?? 0}|${m.teamLeader}` : "";
  const spec = useMemo(() => (m ? specOf(m) : null), [m]);
  useEffect(() => {
    setSel([]); setRole(0); setSecond(-1); setPop(-1); setPicker(false);
    const first = spec && (spec.mode === "pick") && spec.choices.length > 0 && (m?.prompt?.ask === Ask.Team || m?.prompt?.ask === Ask.GiveGun || m?.prompt?.ask === Ask.Bomb)
      ? spec.choices[0]!.value : -1;
    setChoice(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askKey]);
  if (!m) return <div className="bt-wait muted">در حال اتصال به میز…</div>;

  const answered = !!m.prompt?.answered || sent === `${m.stepNo}|${m.prompt?.ask ?? 0}`;
  const live = spec && !(answered && spec.mode !== "hand" && spec.mode !== "suggest") && !m.over ? spec : null;
  const needsTarget = !!live && live.legal.length > 0 && (live.choices.length === 0 || live.needsTarget(choice));
  const needsRole = !!live && live.needsRole(choice);
  const needsSecond = !!live && live.needsSecond(choice);

  const flash = (text: string) => setToast({ text, key: Date.now() });

  const tap = (i: number) => {
    if (!live) {
      // لمسِ چهره: واکنش رو به او (یا ایموجیِ خودم)؛ وقتی واکنش بسته است، فقط نام و نقشِ دانسته
      if (c.canReact()) { setRpop(i); return; }
      const r = m.mseats[i]?.role ?? 0;
      flash(c.seatName(i) + (r ? ` — ${roleFa(r)}` : ""));
      return;
    }
    if (live.mode === "talk") { if (i !== m.me && m.mseats[i]?.alive) setPop(i); return; }
    if (live.mode === "hand") { if (i === m.target) c.setHand(!c.hand.value); return; }
    if (!needsTarget) return;
    if (needsSecond && sel.length >= 1 && second < 0) {
      if (live.legal2.includes(i)) setSecond(i);
      return;
    }
    if (!live.legal.includes(i)) { flash("این نفر را نمی‌شود انتخاب کرد."); return; }
    const max = Math.max(1, live.max);
    let next = sel.includes(i) ? sel.filter((x) => x !== i) : [...sel, i];
    if (next.length > max) next = next.slice(next.length - max);
    setSel(next);
    setSecond(-1);
    if (live.mode === "suggest") { c.suggest(i); return; }
    if (needsRole && next.length === 1 && role === 0) setPicker(true);
  };

  const valid = (): boolean => {
    if (!live) return false;
    if (!needsTarget) return live.choices.length === 0 ? live.optional : choice >= 0;
    if (sel.length < Math.max(1, live.min) || sel.length > Math.max(1, live.max)) return false;
    if (needsRole && role === 0) return false;
    if (needsSecond && second < 0) return false;
    return true;
  };

  const confirm = (ch = choice) => {
    if (!live || !m.prompt) return;
    const ask = m.prompt.ask;
    const [a = -1, b = -1, cc = -1] = sel;
    if (m.night && sel.length > 0) c.markTargets(second >= 0 ? [...sel, second] : sel);
    if (ask === Ask.Team) c.answer({ act: ch, a, role, b: second });
    else if (ask === Ask.GiveGun || ask === Ask.Bomb) c.answer({ choice: ch, a });
    else if (ask === Ask.Mayor) c.answer(ch === 2 ? { choice: 2, a } : { choice: ch });
    else if (ask === Ask.Card) c.answer({ a, b, role });
    else if (live.choices.length > 0 && !(live.legal.length > 0 && live.needsTarget(ch))) c.answer({ choice: ch });
    else c.answer({ a, b, c: cc });
  };

  const pickChoice = (v: number) => {
    setChoice(v); setSel([]); setSecond(-1); setRole(0);
    const ask = m.prompt?.ask;
    // گزینه‌ی بی‌هدف همان دم ثبت می‌شود
    if (live && !(live.legal.length > 0 && live.needsTarget(v)) && ask !== Ask.Team && ask !== Ask.GiveGun && ask !== Ask.Bomb) confirm(v);
  };

  const highlight = (i: number): "legal" | "picked" | "second" | "" => {
    if (!live) return "";
    if (i === second) return "second";
    if (sel.includes(i)) return "picked";
    if (live.mode === "talk") return i !== m.me && m.mseats[i]?.alive ? "legal" : "";
    if (live.mode === "hand") return i === m.target ? "legal" : "";
    if (!needsTarget) return "";
    if (needsSecond && sel.length >= 1 && second < 0) return live.legal2.includes(i) ? "legal" : "";
    return live.legal.includes(i) ? "legal" : "";
  };

  const me = m.mseats[m.me];
  const myRole = !m.watching ? me?.role ?? 0 : 0;
  // خوابِ نیمروزیِ زودیاک: میز مثلِ شب تاریک است
  const noon = !m.over && m.stepKind === StepKind.Noon;

  return (
    <div className={`mf-wrap${phase.night || noon ? " night" : ""}${noon ? " noon" : ""}${m.watching ? " watching" : ""}`}>
      <div className="mf-sky" aria-hidden="true" />
      <header className="mf-banner">
        {!m.watching && myRole > 0 && (
          <button className="mf-rolechip" onClick={() => setRoleCard(true)} aria-label={`نقش تو: ${roleFa(myRole)}`}>
            <RoleEmblem role={myRole} size={34} />
            <span>{roleFa(myRole)}</span>
          </button>
        )}
        <div className="mf-title">
          <i className={`mf-sun${phase.night ? " moon" : ""}`} aria-hidden="true" />
          <b>{m.over ? "پایان بازی" : noon ? NOON_TITLE : phase.night ? nightTitle(phase.day) : dayTitle(phase.day)}</b>
          <span className="mf-scn">
            <i className="scn-ico" aria-hidden="true" style={{ "--ico": `url(/art/mafia/scenario/${SCENARIOS[m.scenario]?.key ?? "classic"}.svg)` } as CSSProperties} />
            {scenarioFa(m.scenario)}
          </span>
        </div>
        <button className="mf-bookbtn" onClick={() => setBook(true)}>دفترچه</button>
      </header>

      <Ring m={m} c={c} tap={tap} highlight={highlight} deal={deal}>
        {/* گوشه‌ی پایینِ حلقه: ایموجی و چت — چت پیش‌تر در سربرگ بود؛ تابلوی پایین جای دکمه‌ی شناور نیست */}
        <div className="mf-cornerbtns">
          {c.canReact() && (
            <button className="mf-reactbtn" onClick={() => setRpop(rpop === m.me ? -1 : m.me)} aria-label="ایموجی" aria-expanded={rpop === m.me}>
              <img src="/art/mafia/react/laugh.svg" alt="" width={30} height={30} />
            </button>
          )}
          {!m.watching && <ChatFab inline />}
        </div>
        {rpop >= 0 && c.canReact() && <ReactPop m={m} c={c} seat={rpop} close={() => setRpop(-1)} />}
      </Ring>

      {/* چتِ زنده درست بالای تابلوی پایین، نه رویش (App آن را برای مافیا نمی‌کشد) — پیامِ تماشاچی با نشان */}
      <div className="mf-chatdock"><ChatLive raised={false} tagWatchers /></div>

      <Panel m={m} c={c} live={live} choice={choice} pickChoice={pickChoice} confirm={() => confirm()} valid={valid()}
             needsTarget={needsTarget} needsRole={needsRole} role={role} openPicker={() => setPicker(true)} sel={sel} />

      {pop >= 0 && live?.mode === "talk" && (
        <div className="mf-saypop" role="menu" aria-label={`درباره‌ی ${c.seatName(pop)}`}>
          <b>{c.seatName(pop)}</b>
          {[Say.Accuse, Say.Trust, Say.VoteFor].map((k) => (
            <button key={k} className={`chip${k === Say.Accuse ? " on" : ""}`} onClick={() => { c.speak(k, pop); setPop(-1); }}>{SAY_LABEL[k]}</button>
          ))}
          <button className="chip" onClick={() => setPop(-1)} aria-label="بستن">×</button>
        </div>
      )}

      <Whisper c={c} />
      {toast && <Toast key={toast.key} text={toast.text} />}

      {picker && live && (
        <Overlay label="حدس نقش" onClose={() => setPicker(false)}>
          <div className="mf-sheet">
            <h3>نقش را انتخاب کن</h3>
            <div className="mf-roles">
              {live.roles.map((r) => (
                <button key={r} className={`mf-roleopt${r === role ? " on" : ""}`} onClick={() => { setRole(r); setPicker(false); }}>
                  <RoleEmblem role={r} size={40} />
                  <span>{roleFa(r)}</span>
                </button>
              ))}
            </div>
          </div>
        </Overlay>
      )}
      {book && <Notebook c={c} onClose={() => setBook(false)} />}
      {reveal && deal !== "deal" && myRole > 0 && <RevealCard key="reveal" m={m} c={c} role={myRole} lift={deal === "card"} />}
      {roleCard && !reveal && myRole > 0 && <RoleCard m={m} c={c} role={myRole} onClose={() => setRoleCard(false)} />}
      {ended && <End m={m} c={c} />}
    </div>
  );
}

// ─────────────────────────── صندلی‌ها ───────────────────────────

function Ring({ m, c, tap, highlight, deal, children }: {
  m: MafiaState; c: MafiaOnline; tap: (i: number) => void; highlight: (i: number) => "legal" | "picked" | "second" | "";
  deal: "" | "deal" | "card"; children?: ReactNode;
}) {
  const stage = useSignal(c.stage);
  const bubbles = useSignal(c.bubbles);
  const sugg = useSignal(c.suggestions);
  const fx = useSignal(c.fx);
  const bomb = useSignal(c.bomb);
  const crew = useSignal(c.crew);
  // عنوانِ بزرگِ شب و روز جای متنِ گرداننده را می‌گیرد؛ تیر و مهر میز را تکان می‌دهند
  const titling = fx.some((f) => f.kind === "title" || f.kind === "finale" || f.kind === "tally");
  const jolt = fx.some((f) => f.kind === "shot" || f.kind === "out" || f.kind === "defense");
  const n = m.seats.length;
  const voting = m.stepKind === StepKind.Vote1 || m.stepKind === StepKind.Vote2;
  const up = voting ? m.mseats.filter((s) => s.hand).length : 0;
  // رأیِ با دست: خطِ طلایی از هر دستِ بالا تا نامزد، تا وقتی دست بالاست (رأیِ مخفی نه)
  const voters = voting && !m.secret && m.target >= 0 ? m.mseats.flatMap((s, i) => (s.hand && i !== m.target ? [i] : [])) : [];
  // نسبتِ قابِ صندلی‌ها، برای چرخاندنِ صندلی‌ها رو به بیرون (صندلی‌ها روی بیضی‌اند، نه دایره)
  const box = useRef<HTMLDivElement>(null);
  const [aspect, setAspect] = useState(1.5);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const read = () => { if (el.clientWidth > 0) setAspect(el.clientHeight / el.clientWidth); };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div className={`mf-ring${titling || deal === "deal" ? " titling" : ""}${jolt ? " jolt" : ""}`} ref={box} style={{ "--n": n } as CSSProperties}>
      {/* میزِ پدرخوانده: رویه‌ی گردویی با ماهوتِ سبز (table.svg)؛ روز چراغ‌ها، شب شمع و تاریکی — style.css */}
      <div className="mf-stage" aria-live="polite">
        <i className="mf-dusk" aria-hidden="true" />
        <span className="who">گرداننده</span>
        {voting && m.target >= 0
          ? <p key={`v${m.stepNo}`}>رأی برای {c.seatName(m.target)}: {fa(up)} از {fa(m.threshold)}</p>
          : stage ? <p key={stage.key}>{stage.text}</p> : null}
      </div>
      <FxLayer m={m} fx={fx} under />
      {m.seats.map((s, i) => {
        const { x, y, a } = seatAt(i, m.me, n);
        const ms = m.mseats[i]!;
        const known = ms.role || (m.over ? ms.startRole : 0);
        const side = known ? roleTeam(known) : ms.side;
        const ring = side === Team.Mafia ? "mafia" : side === Team.Citizen ? "town" : side === Team.Independent ? "indep" : "";
        const win = m.over && known > 0 && side === m.winner;
        const speaking = m.stepKind === StepKind.Talk && m.actor === i;
        const defendant = ms.defendant && (m.stepKind === StepKind.Vote2 || m.stepKind === StepKind.VotePick || m.stepKind === StepKind.Mayor
          || (m.stepKind === StepKind.Talk && m.talk === Talk.Defense));
        const h = highlight(i);
        const bubble = bubbles[i];
        const suggested = Object.entries(sugg).filter(([, t]) => t === i).map(([from]) => Number(from));
        const name = i === m.me && !m.watching ? "تو" : botBare(s.name, s.bot) || `بازیکن ${i + 1}`;
        // پشتیِ صندلی رو به بیرونِ میز: جهتِ صندلی از مرکز، با نسبتِ واقعیِ قاب
        const rot = (Math.atan2(aspect * Math.sin(a), Math.cos(a)) * 180) / Math.PI + 90;
        return (
          <button key={i} className={`mf-seat${ms.alive ? "" : " dead"}${speaking ? " speaking" : ""}${defendant ? " accused" : ""}${h ? ` ${h}` : ""}${ring ? ` ${ring}` : ""}${win ? " win" : ""}`}
                  style={{ left: `${x}%`, top: `${y}%`, "--i": i } as CSSProperties} onClick={() => tap(i)}
                  aria-label={`${name}${ms.alive ? "" : " — بیرون"}${known ? ` — ${roleFa(known)}` : ""}`}>
            <i className="chair" aria-hidden="true" style={{ "--rot": `${rot.toFixed(1)}deg` } as CSSProperties} />
            <span className="face">
              <img src={avatarSrc(s.avatar)} alt="" width={64} height={64} />
              <CountryFlag code={s.country} />
              {!ms.alive && <i className="x" aria-hidden="true" />}
              {known > 0 && <span className="emb"><RoleEmblem role={known} size={26} /></span>}
              {!known && ms.side >= 0 && <i className={`side ${ms.side === Team.Mafia ? "mafia" : "town"}`} aria-hidden="true" />}
              {ms.hand && <i className="hand" aria-label="دست بالا">دست</i>}
            </span>
            <span className="nm">{s.bot && !(i === m.me && !m.watching) && <BotIcon size={12} />}{name}</span>
            <span className="badges">
              {ms.alive && ms.silenced && <i>ساکت</i>}
              {ms.alive && ms.gun && <i className="gun">تفنگ</i>}
              {ms.alive && ms.green && <i className="green">مسیر سبز</i>}
              {ms.alive && ms.red && <i className="red">فرش قرمز</i>}
              {ms.alive && bomb === i && <i className="bomb">بمب</i>}
              {ms.alive && crew.includes(i) && i !== m.me && <i className="crew">هم‌گروه</i>}
              {ms.waiting && !m.night && !voting && <i className="wait">…</i>}
              {suggested.length > 0 && <i className="red">پیشنهاد</i>}
              {m.stepKind === StepKind.Talk && m.challengers.includes(i) && <i className="chal">✋ چالش</i>}
            </span>
            {voting && m.target === i && (
              <span className={`votes${m.stepKind === StepKind.Vote1 && m.threshold > 0 && up >= m.threshold ? " hot" : ""}`}>{fa(up)}</span>
            )}
            {bubble && <span className="mf-bubble">{bubble}</span>}
          </button>
        );
      })}
      <FxLayer m={m} fx={fx} under={false} voters={voters} />
      {deal && <DealIntro m={m} aspect={aspect} lifted={deal === "card"} onDone={() => { if (c.deal.value === "deal") c.deal.value = "card"; }} />}
      {children}
    </div>
  );
}

// ─────────────────────────── پخشِ کارت‌ها (آغاز) ───────────────────────────

/** ضریبِ زمانِ پخش و رو شدنِ کارت: با سرعتِ بازی، ولی نه کندتر از ۱٫۴ برابر — همان --dk ِ style.css. */
const dk = (x: number) => Math.round(x * Math.min(K, 1.4));
const DEAL_START = 420, DEAL_EACH = 110, DEAL_FLY = 340;
const reducedMotion = () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * کارت‌ها از دسته‌ی وسطِ میز یکی‌یکی به هر صندلی (کارتِ من آخر) — همتای MafiaView.Deal ِ اپ. lifted: کارتِ من
 * بلند شده و در RevealCard است؛ بقیه روی میز می‌مانند تا کارت بسته شود.
 */
function DealIntro({ m, aspect, lifted, onDone }: { m: MafiaState; aspect: number; lifted: boolean; onDone: () => void }) {
  const n = m.seats.length;
  const total = DEAL_START + n * DEAL_EACH + DEAL_FLY + 250;
  useEffect(() => {
    const t = setTimeout(onDone, reducedMotion() ? 0 : dk(total));
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const order = Array.from({ length: n }, (_, k) => (m.me + 1 + k) % n);   // دور تا دور، من آخر
  return (
    <div className={`mf-deal${lifted ? " lifted" : ""}`} aria-hidden="true" style={{ "--dealt": `${total - 250}ms` } as CSSProperties}>
      <i className="deck" />
      {order.map((i, k) => {
        const p = seatAt(i, m.me, n);
        const lx = 50 + (p.x - 50) * 0.66, ly = 50 + (p.y - 50) * 0.66;
        const rot = (Math.atan2((p.y - 50) * aspect, p.x - 50) * 180) / Math.PI + 90;
        return (
          <i key={i} className={`dcard${i === m.me ? " mine" : ""}`}
             style={{ "--lx": `${lx}%`, "--ly": `${ly}%`, "--r": `${rot.toFixed(1)}deg`, "--at": `${DEAL_START + k * DEAL_EACH}ms` } as CSSProperties} />
        );
      })}
    </div>
  );
}

/** جای صندلیِ i روی بیضی (درصدِ قاب)، من پایین. */
function seatAt(i: number, me: number, n: number): { x: number; y: number; a: number } {
  const k = ((i - me) % n + n) % n;
  const a = (90 + (k * 360) / n) * (Math.PI / 180);
  return { x: 50 + 41 * Math.cos(a), y: 50 + 41 * Math.sin(a), a };
}

// ─────────────────────────── جلوه‌ها ───────────────────────────

/** زیرِ صندلی‌ها: نورافکن و حوضچه‌ی نورش. باقی روی صندلی‌ها. */
const UNDER = new Set<Fx["kind"]>(["spot"]);
/** چهره کمی بالاتر از وسطِ دکمه‌ی صندلی است (نام و نشان‌ها زیرش) — تا اندازه‌گیری نرسیده. */
const FACE_DY = -10;
/** خطِ بینِ صندلی‌ها از لبه‌ی حلقه‌ی چهره (box-shadow ِ ۳–۴ پیکسلی ِ ‎.face) و کمی فاصله شروع می‌شود و تمام. */
const RING_OUT = 4, LINE_GAP = 6;

type Pt = { x: number; y: number };

/**
 * دو سرِ خطِ بینِ دو صندلی: از لبه‌ی حلقه‌ی یکی تا لبه‌ی حلقه‌ی دیگری، نه از مرکز تا مرکز — وگرنه خط تا
 * وسطِ چهره‌ها می‌رود. همتای MafiaView.EdgeToEdge ِ اپ. صندلی‌های چسبیده جایی برای خط ندارند: null.
 */
export function edgeToEdge(a: Pt, b: Pt, insetA: number, insetB = insetA): { from: Pt; to: Pt } | null {
  const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy);
  if (len <= insetA + insetB + 1) return null;
  const ux = dx / len, uy = dy / len;
  return { from: { x: a.x + ux * insetA, y: a.y + uy * insetA }, to: { x: b.x - ux * insetB, y: b.y - uy * insetB } };
}

/**
 * چهره‌ی یک صندلی: مرکز نسبت به وسطِ دکمه‌ی صندلی، شعاع، و قابِ نام و نشان‌های زیرش (نسبت به مرکزِ چهره، y رو به
 * پایین). از چیدمان (offset*) خوانده می‌شود، نه از getBoundingClientRect که لرزه و پرشِ صندلی را هم می‌خوانَد.
 */
export type FaceGeo = { dx: number; dy: number; r: number; plateTop: number; plateBottom: number; plateHw: number };
const FACE_GUESS: FaceGeo = { dx: 0, dy: FACE_DY, r: 28, plateTop: 30, plateBottom: 48, plateHw: 45 };

/**
 * فاصله‌ی سرِ خط از مرکزِ چهره وقتی خط به سوی u (یکّه، y رو به پایین) بیرون می‌رود: بیرونِ حلقه، و اگر رو به
 * پایین است بیرونِ نام و نشان‌های زیرِ چهره هم — وگرنه خطی که از صندلیِ بالایی پایین می‌آید روی نامش می‌افتد.
 */
export function seatInset(f: FaceGeo, u: Pt): number {
  const base = f.r + RING_OUT + LINE_GAP;
  if (u.y <= 0.01) return base;
  const side = Math.abs(u.x);
  if (side * (f.plateTop / u.y) > f.plateHw) return base;   // از کنارِ پلاک می‌گذرد
  const out = Math.min(f.plateBottom / u.y, side > 0.001 ? f.plateHw / side : Infinity);
  return Math.max(base, out + LINE_GAP * 0.75);
}

function readFaces(ring: HTMLElement): FaceGeo[] {
  return Array.from(ring.querySelectorAll<HTMLElement>(".mf-seat")).map((seat) => {
    const face = seat.querySelector<HTMLElement>(".face");
    if (!face || !seat.offsetWidth) return FACE_GUESS;
    const cy = face.offsetTop + face.offsetHeight / 2;
    // نام همیشه هست؛ ردیفِ نشان‌ها فقط وقتی چیزی در آن است
    const below = [seat.querySelector<HTMLElement>(".nm"), seat.querySelector<HTMLElement>(".badges")]
      .filter((el): el is HTMLElement => !!el && el.offsetHeight > 0 && (el.classList.contains("nm") || el.childElementCount > 0));
    return {
      dx: face.offsetLeft + face.offsetWidth / 2 - seat.offsetWidth / 2,
      dy: cy - seat.offsetHeight / 2,
      r: face.offsetWidth / 2,
      plateTop: below.length ? Math.min(...below.map((el) => el.offsetTop)) - cy : face.offsetHeight / 2,
      plateBottom: below.length ? Math.max(...below.map((el) => el.offsetTop + el.offsetHeight)) - cy : face.offsetHeight / 2,
      plateHw: below.length ? Math.max(...below.map((el) => el.offsetWidth)) / 2 : 0,
    };
  });
}
const sameFaces = (a: FaceGeo[], b: FaceGeo[]) =>
  a.length === b.length && a.every((f, i) => {
    const g = b[i]!;
    return Math.abs(f.dx - g.dx) < 0.5 && Math.abs(f.dy - g.dy) < 0.5 && Math.abs(f.r - g.r) < 0.5
      && Math.abs(f.plateBottom - g.plateBottom) < 0.5 && Math.abs(f.plateHw - g.plateHw) < 0.5 && Math.abs(f.plateTop - g.plateTop) < 0.5;
  });

/** تکه‌های ترکشِ جشن: پخشِ ثابت (نه تصادفی) تا هر بار همان شکل باشد. */
const CONFETTI = Array.from({ length: 28 }, (_, i) => {
  const ang = (i / 28) * Math.PI * 2 + (i % 3) * 0.35;
  const sp = 90 + ((i * 37) % 70);
  return { x: Math.cos(ang) * sp * 1.4, y: Math.sin(ang) * sp - 60, r: (i * 97) % 360, d: (i % 5) * 40, c: i % 4 };
});

function FxLayer({ m, fx, under, voters = [] }: { m: MafiaState; fx: Fx[]; under: boolean; voters?: number[] }) {
  const box = useRef<HTMLDivElement>(null);
  const [geo, setGeo] = useState({ w: 0, h: 0 });
  const [faces, setFaces] = useState<FaceGeo[]>([]);
  // اندازه‌ی زنده‌ی قاب برای خط‌ها: تابلوی پایین بلند و کوتاه می‌شود و میز با آن (flex)، پس یک بار سنجیدن کافی نیست
  useLayoutEffect(() => {
    const el = box.current;
    if (!el || under) return;
    const read = () => setGeo({ w: el.clientWidth, h: el.clientHeight });
    read();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [under]);
  // جای چهره‌ها پس از هر کشیدن: نشان‌های زیرِ چهره (تفنگ، ساکت، …) با هر گام می‌آیند و می‌روند و چهره را جابه‌جا می‌کنند
  useLayoutEffect(() => {
    const ring = box.current?.parentElement;
    if (!ring || under) return;
    const now = readFaces(ring);
    setFaces((was) => (sameFaces(was, now) ? was : now));
  });
  const n = m.seats.length;
  const list = fx.filter((f) => UNDER.has(f.kind) === under);
  const at = (s: number): CSSProperties => {
    const p = seatAt(s, m.me, n);
    return { left: `${p.x}%`, top: `${p.y}%` };
  };
  const nameOf = (i: number) => (i === m.me && !m.watching ? "تو" : botText(m.seats[i]?.name || `بازیکن ${i + 1}`, m.seats[i]?.bot));
  /** مرکزِ چهره‌ی صندلیِ i در قاب (پیکسل). */
  const faceAt = (i: number): Pt => {
    const p = seatAt(i, m.me, n), f = faces[i];
    return { x: (p.x / 100) * geo.w + (f?.dx ?? 0), y: (p.y / 100) * geo.h + (f?.dy ?? FACE_DY) };
  };
  /** خطِ بینِ دو صندلی (اتهام، رأی، ردِ گلوله)، از لبه‌ی حلقه‌ی چهره تا لبه‌ی حلقه‌ی چهره. */
  const lineStyle = (from: number, to: number): CSSProperties | null => {
    if (!geo.w) return null;
    const a = faceAt(from), b = faceAt(to), len = Math.hypot(b.x - a.x, b.y - a.y);
    if (!len) return null;
    const u = { x: (b.x - a.x) / len, y: (b.y - a.y) / len };
    const ff = faces[from] ?? FACE_GUESS, ft = faces[to] ?? FACE_GUESS;
    // میزِ شلوغ: اگر دور زدنِ نام سرِ خط را به حلقه‌ی همسایه ببرد، همان لبه‌ی حلقه‌ی خودش — همتای MafiaView.SeatLine
    const inRing = (p: Pt) => Array.from({ length: n }, (_, i) => i).some((i) => {
      const c = faceAt(i), r = (faces[i] ?? FACE_GUESS).r + RING_OUT + 2;
      return Math.hypot(p.x - c.x, p.y - c.y) < r;
    });
    let e = edgeToEdge(a, b, seatInset(ff, u), seatInset(ft, { x: -u.x, y: -u.y }));
    if (!e || inRing(e.from) || inRing(e.to)) e = edgeToEdge(a, b, ff.r + RING_OUT + LINE_GAP, ft.r + RING_OUT + LINE_GAP);
    if (!e) return null;
    const ang = Math.atan2(e.to.y - e.from.y, e.to.x - e.from.x);
    return { left: e.from.x, top: e.from.y, width: Math.hypot(e.to.x - e.from.x, e.to.y - e.from.y), transform: `rotate(${ang}rad)` };
  };
  return (
    <div className={`mf-fx${under ? " under" : ""}`} ref={box} aria-hidden="true">
      {list.map((f) => {
        switch (f.kind) {
          case "title":
            return (
              <div key={f.key} className={`mf-fx-title ${f.night ? "night" : "day"}`}>
                <i className="orb" /><b>{f.text}</b>
              </div>
            );
          case "finale":
            return (
              <div key={f.key} className={`mf-fx-finale ${f.tone}`}>
                <b>{f.text}</b>
                <span className="confetti">
                  {CONFETTI.map((p, i) => (
                    <i key={i} className={`k${p.c}`}
                       style={{ "--x": `${p.x}px`, "--y": `${p.y}px`, "--r": `${p.r}deg`, animationDelay: `calc(${p.d}ms * var(--k, 1))` } as CSSProperties} />
                  ))}
                </span>
              </div>
            );
          case "shot":
            return (
              <i key={f.key} className={`mf-fx-shot ${f.tone}`} style={at(f.seat)}>
                <i className="wave" /><i className="flash" />
                <svg className="crack" viewBox="-50 -50 100 100">
                  <path pathLength={1} d="M6 4 L38 -14 L46 -10 M6 4 L-8 40 M6 4 L-38 -4 L-44 4 M6 4 L20 -40" />
                </svg>
              </i>
            );
          case "out": case "defense":
            return <b key={f.key} className={`mf-fx-stamp ${f.tone}`} style={at(f.seat)}>{f.kind === "out" ? "بیرون" : "دفاع"}</b>;
          case "halo":
            return (
              <i key={f.key} className="mf-fx-halo" style={at(f.seat)}>
                <i className="ring" />
                {[0, 1, 2, 3, 4, 5].map((k) => <i key={k} className="spark" style={{ "--k": k } as CSSProperties} />)}
              </i>
            );
          case "target":
            return <i key={f.key} className={`mf-fx-target ${f.tone}`} style={at(f.seat)} />;
          case "shield":
            return <i key={f.key} className={`mf-fx-shield ${f.tone}`}><i className="ring" /><i className="disc" /></i>;
          case "spot": {
            const p = seatAt(f.seat, m.me, n);
            return (
              <span key={f.key} className={`mf-fx-spot ${f.tone}`}>
                <i className="beam" style={{ left: `${p.x}%`, height: `${p.y}%` }} />
                <i className="pool" style={{ left: `${p.x}%`, top: `${p.y}%` }} />
              </span>
            );
          }
          case "react": {
            const src = `/art/mafia/react/${REACTIONS[f.n]?.key ?? "laugh"}.svg`;
            if (f.to < 0) return <i key={f.key} className="mf-fx-react" style={at(f.seat)}><img src={src} alt="" /></i>;
            // پرتاب از فرستنده تا هدف
            const a = seatAt(f.seat, m.me, n), b = seatAt(f.to, m.me, n);
            const dx = ((b.x - a.x) / 100) * geo.w, dy = ((b.y - a.y) / 100) * geo.h;
            return (
              <i key={f.key} className="mf-fx-react fly" style={{ ...at(f.seat), "--dx": `${dx}px`, "--dy": `${dy}px` } as CSSProperties}>
                <img src={src} alt="" />
              </i>
            );
          }
          case "line": case "gun": {
            const st = lineStyle(f.seat, f.to);
            return st && <i key={f.key} className={`mf-fx-line ${f.kind} ${f.tone}`} style={st}><i /></i>;
          }
          case "tally": {
            const kind = Number(f.text);
            const title = (kind === 1 ? "رأی اول برای " : kind === 3 ? "بازجویی: " : "رأی خروج برای ") + nameOf(f.seat);
            return (
              <Fragment key={f.key}>
              <div className={`mf-fx-tally ${f.tone}`} role="status">
                <b className="t">{title}</b>
                <span className="n">{fa(f.n)}<small> رأی</small></span>
                {f.list.length > 0
                  ? <span className="who">{f.list.map((v, j) => <i key={v} style={{ "--j": j } as CSSProperties}>{nameOf(v)}</i>)}</span>
                  : <span className="who secret">رأی مخفی — فقط تعداد اعلام می‌شود</span>}
                {kind === 1 && (
                  <span className="res" style={{ "--j": f.list.length } as CSSProperties}>
                    {f.tone === "mafia" ? "به دفاع می‌رود" : `به نصاب (${fa(m.threshold)}) نرسید`}
                  </span>
                )}
              </div>
              {/* صندلیِ هر رأی‌دهنده به نوبت می‌درخشد، هم‌زمان با نامش */}
              {f.list.map((v, j) => <i key={`p${v}`} className="mf-fx-voter" style={{ ...at(v), "--j": j } as CSSProperties} />)}
              </Fragment>
            );
          }
        }
        return null;
      })}
      {!under && voters.map((v) => {
        const st = lineStyle(v, m.target);
        return st && <i key={`v${v}-${m.target}`} className="mf-fx-line vote gold" style={st}><i /></i>;
      })}
    </div>
  );
}

export function RoleEmblem({ role, size }: { role: number; size: number }) {
  const t = roleTeam(role);
  return (
    <span className={`mf-emb ${t === Team.Mafia ? "mafia" : t === Team.Independent ? "indep" : "town"}`} style={{ width: size, height: size }}>
      <img src={`/art/mafia/${roleKey(role)}.svg`} alt="" width={size} height={size} />
    </span>
  );
}

// ─────────────────────────── تابلوی پایین ───────────────────────────

function Panel({ m, c, live, choice, pickChoice, confirm, valid, needsTarget, needsRole, role, openPicker, sel }: {
  m: MafiaState; c: MafiaOnline; live: Spec | null; choice: number; pickChoice: (v: number) => void; confirm: () => void;
  valid: boolean; needsTarget: boolean; needsRole: boolean; role: number; openPicker: () => void; sel: number[];
}) {
  const timer = useSignal(c.timer);
  const hand = useSignal(c.hand);
  const t = timer && live ? Math.max(0, Math.min(1, timer.left / Math.max(0.1, timer.total))) : 0;
  const clock = timer && live ? (
    <i className={`mf-clock${t < 0.25 ? " low" : ""}`} style={{ "--f": t } as CSSProperties} aria-label="مهلت">{fa(Math.ceil(timer.left))}</i>
  ) : null;
  if (!live) {
    const asked = !m.watching && m.challengers.includes(m.me);
    return (
      <div className="mf-panel idle">
        <p>{idleText(m, c)}</p>
        {m.challengeTo === m.me && !m.watching
          ? <p className="mf-chal on">✋ {c.seatName(m.actor)} چالش را به تو داد — بعد از او نوبت توست</p>
          : m.canChallenge
            ? <button className="btn mf-chalbtn" onClick={() => c.askChallenge()}>✋ چالش می‌خواهم</button>
            : asked && <p className="mf-chal">✋ چالش خواستی — منتظر {c.seatName(m.actor)}</p>}
      </div>
    );
  }
  if (live.mode === "talk") {
    const opts = m.talk === Talk.Defense || m.talk === Talk.Interrogation ? [Say.Defend, Say.Citizen] : [Say.Citizen, Say.Unsure];
    return (
      <div className="mf-panel ask">
        {clock}
        <h4>{live.title}</h4>
        <p className="sub">چهره‌ی کسی را لمس کن تا درباره‌اش بگویی · {fa(m.saysLeft)} جمله‌ی دیگر</p>
        <div className="chips">
          {opts.map((k) => <button key={k} className="chip" onClick={() => c.speak(k)}>{SAY_LABEL[k]}</button>)}
        </div>
        {m.talk === Talk.Day && (m.challengeTo >= 0
          ? <p className="mf-chal on">✋ چالش با {c.seatName(m.challengeTo)} — بعد از تو</p>
          : m.challengers.length > 0 && (
            <div className="mf-chal" role="group" aria-label="تایم چالش">
              <span>✋ چالش می‌خواهند:</span>
              {m.challengers.map((x) => (
                <button key={x} className="chip gold" onClick={() => c.giveChallenge(x)}>چالش به {c.seatName(x)}</button>
              ))}
            </div>
          ))}
        <button className="btn" onClick={() => c.endTalk()}>تمام</button>
      </div>
    );
  }
  if (live.mode === "hand") {
    return (
      <div className="mf-panel ask">
        {clock}
        <h4>برای {c.seatName(m.target)} دست بالا می‌بری؟</h4>
        <button className={`btn${hand ? "" : " ghost"} mf-handbtn`} aria-pressed={hand} onClick={() => c.setHand(!hand)}>
          {hand ? "دستم بالاست" : "دست بالا"}
        </button>
      </div>
    );
  }
  const hint = needsRole && sel.length >= 1
    ? (role ? `حدس: ${roleFa(role)}` : "نقش را انتخاب کن.")
    : needsTarget && live.max > 1 ? `انتخاب: ${fa(sel.length)} از ${fa(live.max)}`
    : sel.length === 1 ? c.seatName(sel[0]!) : "";
  return (
    <div className="mf-panel ask">
      {clock}
      <h4>{live.title}</h4>
      {hint && <p className="sub">{hint}</p>}
      {live.choices.length > 0 && (
        <div className="chips" role="radiogroup">
          {live.choices.map((ch) => (
            <button key={ch.value} role="radio" aria-checked={choice === ch.value} className={`chip${choice === ch.value ? " on" : ""}`}
                    onClick={() => pickChoice(ch.value)}>{ch.label}</button>
          ))}
        </div>
      )}
      {needsRole && sel.length >= 1 && <button className="chip" onClick={openPicker}>{role ? roleFa(role) : "حدس نقش…"}</button>}
      <div className="row">
        {live.mode === "suggest"
          ? <p className="sub">هدف پیشنهادی را لمس کن — تصمیم با اوست.</p>
          : (needsTarget || live.choices.length === 0) && (
            <button className="btn" disabled={!valid} onClick={confirm}>تأیید</button>
          )}
        {live.optional && live.mode !== "suggest" && <button className="btn ghost" onClick={() => c.pass()}>هیچ</button>}
      </div>
    </div>
  );
}

function idleText(m: MafiaState, c: MafiaOnline): string {
  if (m.over) return "";
  if (m.watching) return m.night ? "شب است؛ شهر خواب است." : "در حال تماشا — نقش‌ها پنهان‌اند.";
  if (m.revealing) return m.mseats[m.me]?.waiting ? "کارت‌ها پخش می‌شوند…" : "منتظر بقیه تا کارتشان را ببینند…";
  if (!m.mseats[m.me]?.alive) return "تو بیرون رفتی — تماشا کن.";
  if (m.prompt?.blocked) return "امشب مسدودی؛ کارت اثری ندارد.";
  if (m.night) return "شب است… چشم‌هایت بسته.";
  if (m.stepKind === StepKind.Noon) return "خواب نیمروزی… چشم‌هایت بسته.";
  if (m.stepKind === StepKind.Bomb && m.actor >= 0) return `${c.seatName(m.actor)} یکی از سیم‌ها را می‌بُرد…`;
  if (m.stepKind === StepKind.Talk && m.actor >= 0) return `${c.seatName(m.actor)} حرف می‌زند…`;
  return "گوش کن و به چهره‌ها دقت کن.";
}

/** واکنش‌ها: رو به یک نفر هر هشت تا (لایک و دیس‌لایک اول)، برای خودم فقط ایموجی‌ها بالای صندلیِ خودم. */
function ReactPop({ m, c, seat, close }: { m: MafiaState; c: MafiaOnline; seat: number; close: () => void }) {
  const self = seat === m.me;
  const r = m.mseats[seat]?.role ?? 0;
  const list = REACTIONS.map((x, code) => ({ ...x, code })).filter((x) => !self || x.code >= 2);
  return (
    <div className="mf-reactpop" role="menu" aria-label={self ? "ایموجی" : `واکنش به ${c.seatName(seat)}`}>
      <b>{self ? "ایموجی" : c.seatName(seat) + (r ? ` — ${roleFa(r)}` : "")}</b>
      <div className="row">
        {list.map((x) => <ReactBtn key={x.key} c={c} x={x} target={self ? -1 : seat} close={close} />)}
        <button className="chip" onClick={close} aria-label="بستن">×</button>
      </div>
    </div>
  );
}

/**
 * یک واکنش در پنجره. پس از لمس کم‌رنگ می‌ماند تا سرعت‌گیر دوباره راهش بدهد (همان واکنش ۴ ثانیه، بقیه فقط
 * وقتی سه تا پشتِ هم رفته) — لمسِ پشتِ هم برای بقیه‌ی میز یک صدا و یک ایموجی است، نه ده تا.
 */
function ReactBtn({ c, x, target, close }:
  { c: MafiaOnline; x: { key: string; fa: string; code: number }; target: number; close: () => void }) {
  const until = useCool(c.reactPace, reactKey(x.code, target));
  const cool = coolProps(until);
  return (
    <button className={`mf-react ${x.key}${cool.className}`} style={cool.style} aria-label={x.fa} title={x.fa}
            aria-disabled={until ? true : undefined}
            onClick={() => { if (until) return; c.react(x.code, target); close(); }}>
      <img src={`/art/mafia/react/${x.key}.svg`} alt="" width={34} height={34} />
    </button>
  );
}

function Whisper({ c }: { c: MafiaOnline }) {
  const w = useSignal(c.whisper);
  if (!w) return null;
  return <div key={w.key} className="mf-whisper" role="status">{w.text}</div>;
}

function Toast({ text }: { text: string }) {
  const [on, setOn] = useState(true);
  useEffect(() => { const t = setTimeout(() => setOn(false), 1800); return () => clearTimeout(t); }, []);
  return on ? <div className="mf-toast" role="status">{text}</div> : null;
}

// ─────────────────────────── برگه‌ها ───────────────────────────

function Notebook({ c, onClose }: { c: MafiaOnline; onClose: () => void }) {
  const log = useSignal(c.log);
  return (
    <Overlay label="دفترچه" onClose={onClose}>
      <div className="mf-sheet mf-book">
        <h3>دفترچه</h3>
        {log.length === 0 ? <p className="muted">هنوز چیزی ننوشته‌ای.</p> : (
          <ol>
            {log.map((l, i) => <li key={i} className={l.secret ? "secret" : ""}><i>{dayTitle(l.day)}</i>{l.text}</li>)}
          </ol>
        )}
        <button className="btn" onClick={onClose}>بستن</button>
      </div>
    </Overlay>
  );
}

/** روی کارتِ نقش: سمت، نماد، نام، شرح و هم‌تیمی‌ها. */
function RoleFace({ m, c, role }: { m: MafiaState; c: MafiaOnline; role: number }) {
  const t = roleTeam(role);
  const mates = m.mates.map((i) => c.seatName(i) + (m.mseats[i]?.alive ? "" : " (بیرون)"));
  return (
    <>
      <span className="team">{teamFa(t)}</span>
      <RoleEmblem role={role} size={112} />
      <h3>{roleFa(role)}</h3>
      <p>{roleDesc(role)}</p>
      {isMafiaRole(role) && <p className="mates">{mates.length ? `هم‌تیمی‌ها: ${mates.join("، ")}` : "تو تنها مافیایی."}</p>}
    </>
  );
}

const sideClass = (role: number) => { const t = roleTeam(role); return t === Team.Mafia ? "mafia" : t === Team.Independent ? "indep" : "town"; };

/** چیپِ نقش: همان کارت، هر وقت بخواهی. */
function RoleCard({ m, c, role, onClose }: { m: MafiaState; c: MafiaOnline; role: number; onClose: () => void }) {
  return (
    <Overlay label="نقش تو" onClose={onClose}>
      <div className={`mf-sheet mf-rolecard ${sideClass(role)}`}>
        <RoleFace m={m} c={c} role={role} />
        <button className="btn" onClick={onClose}>فهمیدم</button>
      </div>
    </Overlay>
  );
}

/**
 * کارتِ نقشِ آغاز: کارتِ پخش‌شده‌ی من بلند می‌شود (lift)، وسط بزرگ می‌شود و برمی‌گردد؛ «فهمیدم» فقط پس از رو شدن.
 * با «فهمیدم» سرور خبردار می‌شود (بازی تا «فهمیدم» ِ همه شروع نمی‌شود) و کارت در چیپِ نقش می‌نشیند.
 */
function RevealCard({ m, c, role, lift }: { m: MafiaState; c: MafiaOnline; role: number; lift: boolean }) {
  const still = !lift || reducedMotion();
  const [flipped, setFlipped] = useState(still);
  const [ready, setReady] = useState(still);
  const [closing, setClosing] = useState(false);
  useEffect(() => {
    if (still) return;
    const a = setTimeout(() => setFlipped(true), dk(750));
    const b = setTimeout(() => setReady(true), dk(750 + 650));
    return () => { clearTimeout(a); clearTimeout(b); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const box = useRef<HTMLDivElement>(null);
  const [fly, setFly] = useState({ x: 0, y: 0 });
  const ok = () => {
    if (closing) return;
    c.seenCard();
    if (reducedMotion()) { c.closeReveal(); return; }
    // کارت در چیپِ نقشِ بالای میز می‌نشیند — همان‌جا که بعداً دوباره بازش می‌کنی
    const chip = document.querySelector(".mf-rolechip")?.getBoundingClientRect();
    const me = box.current?.getBoundingClientRect();
    if (chip && me && me.width > 0) setFly({ x: chip.left + chip.width / 2 - (me.left + me.width / 2), y: chip.top + chip.height / 2 - (me.top + me.height / 2) });
    setClosing(true);
    setTimeout(() => c.closeReveal(), dk(450));
  };
  const card = (
    <div ref={box} className={`mf-card3d${lift && !still ? " lift" : ""}${flipped ? " flipped" : ""}`}
         style={{ "--fx": `${fly.x}px`, "--fy": `${fly.y}px` } as CSSProperties}>
      <div className="inner">
        <div className="back"><b>نقش تو</b></div>
        <div className={`front mf-sheet mf-rolecard ${sideClass(role)}`}>
          <RoleFace m={m} c={c} role={role} />
          <p className="hint">نقشت را به کسی نشان نده.</p>
        </div>
      </div>
    </div>
  );
  if (closing) return <div className="mf-reveal closing" aria-hidden="true">{card}</div>;
  return (
    <Overlay label="نقش تو" base="veil" className="mf-reveal">
      {card}
      {ready ? <button className="btn mf-gotit" onClick={ok}>فهمیدم</button> : <span className="mf-gotit-space" />}
    </Overlay>
  );
}

function End({ m, c }: { m: MafiaState; c: MafiaOnline }) {
  const town = m.winner === Team.Citizen;
  const title = m.watching ? (town ? "شهروندها بردند" : "مافیا برد")
    : m.won ? (town ? "شهر پاک شد — بردی!" : "مافیا شهر را گرفت — بردی!") : (town ? "شهروندها بردند" : "مافیا برد");
  return (
    <Overlay label="پایان بازی" after={<ResultAd won={m.watching ? null : m.won} watching={m.watching} />}>
      <div className={`mf-sheet mf-end${m.won ? " won" : ""}`}>
        <h3>{title}</h3>
        <ul>
          {m.mseats.map((s, i) => {
            const r = s.startRole || s.role;
            return (
              <li key={i} className={isMafiaRole(r) ? "mafia" : ""}>
                {r > 0 && <RoleEmblem role={r} size={28} />}
                <b>{c.seatName(i)}</b>
                <span>{roleFa(r)}</span>
                {(s.likes > 0 || s.dislikes > 0) && (
                  <span className="likes" aria-label={`${fa(s.likes)} لایک، ${fa(s.dislikes)} دیس‌لایک`}>
                    <img src="/art/mafia/react/like.svg" alt="" width={16} height={16} />{fa(s.likes)}
                    <img src="/art/mafia/react/dislike.svg" alt="" width={16} height={16} />{fa(s.dislikes)}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
        {/* «شب‌های مافیا»: امتیازِ جام و «بازیِ بعدی در جام» */}
        {!m.watching && <CupAfter />}
        <button className="btn" onClick={() => client.exitTable()}>برگرد</button>
      </div>
    </Overlay>
  );
}
